import { useState, useMemo, useEffect } from "react";
import { type AutomaticStreakConfig, type CampaignAutopilotConfigV1Data } from "@cede/types";
import { useAutomaticStreakBalance } from "./useAutomaticStreakBalance";
import Decimal from "decimal.js";

export interface AutomaticStreakConfigFormData {
  totalTrades: number;
  volumePerCycle: string; //Single volume input
  intervalSeconds: number;
  scheduledStartTime: Date;
  alsoRunForNextEpoch: boolean; // Create streak for next epoch too
}

/** Config can be either the full AutomaticStreakConfig or just the V1 data fields */
type ConfigInput = AutomaticStreakConfig | CampaignAutopilotConfigV1Data;

interface UseAutomaticStreakConfigFormProps {
  accountId: string;
  exchangeId: string;
  pairSymbol: string;
  config: ConfigInput;
  onSubmit: (data: AutomaticStreakConfigFormData) => void;
  onValidityChange?: (isValid: boolean) => void;
}

interface IntervalOption {
  value: number;
  label: string;
}

const formatIntervalLabel = (seconds: number): string => {
  if (seconds < 60) return `${seconds}s`;
  const minutes = seconds / 60;
  return `${minutes}m`;
};

export const useAutomaticStreakConfigForm = ({
  accountId,
  exchangeId,
  pairSymbol,
  config,
  onSubmit,
  onValidityChange,
}: UseAutomaticStreakConfigFormProps) => {
  // Balance hook
  const {
    balance,
    quoteToken,
    isLoading: isBalanceLoading,
    hasEnoughBalance,
    getRequiredBalance,
    getMaxVolumePerTrade,
  } = useAutomaticStreakBalance({
    accountId,
    exchangeId,
    pairSymbol,
  });

  // Form field states - store as strings to allow clearing during editing
  const [totalTradesInput, setTotalTradesInput] = useState<string>(String(config.defaultCycles));
  const [intervalSeconds, setIntervalSeconds] = useState<number>(
    config.validIntervals[1] ?? config.validIntervals[0] ?? 30,
  );
  const [startDelayInput, setStartDelayInput] = useState<string>(String(config.defaultStartDelayMinutes));
  const [alsoRunForNextEpoch, setAlsoRunForNextEpoch] = useState<boolean>(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Volume per cycle: derive recommended value, track user edits
  // This avoids useEffect for syncing state with async balance data
  const [userVolumePerCycle, setUserVolumePerCycle] = useState<string>("");
  const [hasUserEditedVolume, setHasUserEditedVolume] = useState(false);

  // Parsed values for calculations
  const totalTrades = parseInt(totalTradesInput, 10) || 0;
  const startDelayMinutes = parseInt(startDelayInput, 10) || 0;

  // Calculate the variance range for display using Decimal.js for precision
  // volumeVariancePercent is only present in full AutomaticStreakConfig, not in CampaignAutopilotConfigV1Data
  const variancePercent = "volumeVariancePercent" in config ? (config.volumeVariancePercent ?? 25) : 25;
  const varianceFactor = new Decimal(variancePercent).dividedBy(100);

  // Calculate the minimum allowed volumePerCycle so that even with variance,
  // the lower bound is always >= minVolumePerTrade
  // Formula: minVolumePerCycle = minVolumePerTrade / (1 - variancePercent/100)
  // Round UP to 2 decimal places to avoid browser validation issues with repeating decimals
  const minVolumePerCycle = new Decimal(config.minVolumePerTrade)
    .dividedBy(new Decimal(1).minus(varianceFactor))
    .toDecimalPlaces(2, Decimal.ROUND_UP);

  // Balance-based max volume (use volumeMax for conservative check)
  const maxAllowedVolume = getMaxVolumePerTrade(totalTrades);

  // Derive recommended volume from balance (recalculates when balance/config changes)
  const recommendedVolume = useMemo(() => {
    if (balance <= 0) return config.defaultVolumePerTrade.toFixed(2);
    const maxFromBalance = getMaxVolumePerTrade(totalTrades);
    const recommended = Math.min(maxFromBalance, config.maxVolumePerTrade, config.defaultVolumePerTrade);
    const minVolume = minVolumePerCycle.toNumber();
    return Math.max(recommended, minVolume).toFixed(2);
  }, [
    balance,
    totalTrades,
    config.maxVolumePerTrade,
    config.defaultVolumePerTrade,
    minVolumePerCycle,
    getMaxVolumePerTrade,
  ]);

  // Effective volume: use derived recommendation until user edits, then use user's value
  const volumePerCycle = hasUserEditedVolume ? userVolumePerCycle : recommendedVolume;

  // Wrapper setter that tracks user edits
  const setVolumePerCycle = (value: string) => {
    setHasUserEditedVolume(true);
    setUserVolumePerCycle(value);
  };

  // Volume calculations (must come after volumePerCycle is defined)
  const volume = new Decimal(volumePerCycle || 0);
  const volumeMin = volume.times(new Decimal(1).minus(varianceFactor));
  const volumeMax = volume.times(new Decimal(1).plus(varianceFactor));

  // Interval options from config
  const intervalOptions = useMemo<IntervalOption[]>(
    () =>
      config.validIntervals.map((value) => ({
        value,
        label: formatIntervalLabel(value),
      })),
    [config.validIntervals],
  );

  // Scheduled start time - for display only (actual time calculated at submission)
  const scheduledStartTimePreview = useMemo(() => {
    return new Date(Date.now() + startDelayMinutes * 60 * 1000);
  }, [startDelayMinutes]);

  // Calculated totals
  const totalDurationSeconds = totalTrades * intervalSeconds;
  const avgVolumePerTrade = volume.toNumber(); // Use the single volume value (backend adds variance)
  const totalVolumeEstimate = volume.times(totalTrades).toNumber();

  // Balance validation (use volumeMax for conservative check)
  const volumeMaxNumber = volumeMax.toNumber();
  const requiredBalance = getRequiredBalance(volumeMaxNumber, totalTrades);
  const isBalanceSufficient = hasEnoughBalance(volumeMaxNumber, totalTrades);

  // Validation errors -Simplified validation with single volume input
  const validationErrors = useMemo(() => {
    const newErrors: Record<string, string> = {};

    if (totalTrades < config.minTrades || totalTrades > config.maxTrades) {
      newErrors.totalTrades = `Must be ${config.minTrades}-${config.maxTrades}`;
    }

    //Single volume validation - use minVolumePerCycle which accounts for variance
    if (volume.isNaN() || volume.lessThanOrEqualTo(0)) {
      newErrors.volumePerCycle = "Required";
    } else if (volume.lessThan(minVolumePerCycle)) {
      newErrors.volumePerCycle = `Min ${minVolumePerCycle.toFixed(2)} ${quoteToken}`;
    } else if (volume.greaterThan(config.maxVolumePerTrade)) {
      newErrors.volumePerCycle = `Max ${config.maxVolumePerTrade} ${quoteToken}`;
    } else if (volumeMax.greaterThan(maxAllowedVolume)) {
      // Check if volumeMax (with variance) exceeds balance
      newErrors.volumePerCycle = `Exceeds available balance (max ~${maxAllowedVolume.toFixed(2)} ${quoteToken})`;
    }

    if (!config.validIntervals.includes(intervalSeconds)) {
      newErrors.intervalSeconds = "Invalid interval";
    }

    if (startDelayMinutes < 0) {
      newErrors.startDelay = "Cannot be negative";
    }

    return newErrors;
  }, [
    totalTrades,
    volume,
    volumeMax,
    intervalSeconds,
    startDelayMinutes,
    config,
    quoteToken,
    maxAllowedVolume,
    minVolumePerCycle,
  ]);

  // Form validity -Simplified with single volume
  const isFormValid = useMemo(() => {
    return (
      Object.keys(validationErrors).length === 0 &&
      totalTrades > 0 &&
      volume.greaterThan(0) &&
      isBalanceSufficient &&
      !isBalanceLoading
    );
  }, [validationErrors, totalTrades, volume, isBalanceSufficient, isBalanceLoading]);

  // Notify parent of validity changes
  useEffect(() => {
    onValidityChange?.(isFormValid);
  }, [isFormValid, onValidityChange]);

  // Handle field blur - show validation errors
  const handleFieldBlur = () => {
    setErrors(validationErrors);
  };

  // Handle form submission - Submit single volumePerCycle
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!isFormValid) {
      setErrors(validationErrors);
      return;
    }

    // Calculate scheduledStartTime at submission time to avoid stale times
    // if the user spends time filling out the form
    const scheduledStartTime = new Date(Date.now() + startDelayMinutes * 60 * 1000);

    onSubmit({
      totalTrades,
      volumePerCycle,
      intervalSeconds,
      scheduledStartTime,
      alsoRunForNextEpoch,
    });
  };

  return {
    // Form field values (strings for inputs, numbers for display/calculations)
    fields: {
      totalTrades: totalTradesInput,
      totalTradesParsed: totalTrades,
      volumePerCycle, //Single volume input
      volumePerCycleParsed: volume.toNumber(), //Parsed value (converted from Decimal)
      volumeMin: volumeMin.toNumber(), //Calculated min for display (converted from Decimal)
      volumeMax: volumeMaxNumber, //Calculated max for display (already converted)
      variancePercent, //Variance percentage from config
      intervalSeconds,
      startDelayMinutes: startDelayInput,
      startDelayParsed: startDelayMinutes,
      alsoRunForNextEpoch,
    },
    // Form field setters
    setters: {
      setTotalTrades: setTotalTradesInput,
      setVolumePerCycle, //Single volume setter
      setIntervalSeconds,
      setStartDelayMinutes: setStartDelayInput,
      setAlsoRunForNextEpoch,
    },
    // Balance info
    balance: {
      value: balance,
      quoteToken,
      isLoading: isBalanceLoading,
      maxAllowedVolume,
      requiredBalance,
      isSufficient: isBalanceSufficient,
    },
    // Calculated values
    calculations: {
      totalDurationSeconds,
      avgVolumePerTrade,
      totalVolumeEstimate,
      scheduledStartTime: scheduledStartTimePreview, // Preview for display (actual calculated at submit)
    },
    // Validation
    validation: {
      errors,
      validationErrors,
      isFormValid,
    },
    // Options
    intervalOptions,
    // Config bounds (for display)
    configBounds: {
      minTrades: config.minTrades,
      maxTrades: config.maxTrades,
      minVolumePerTrade: config.minVolumePerTrade,
      maxVolumePerTrade: config.maxVolumePerTrade,
      // Minimum volumePerCycle that accounts for variance (so lower bound >= minVolumePerTrade)
      minVolumePerCycle: minVolumePerCycle.toNumber(),
    },
    // Handlers
    handleFieldBlur,
    handleSubmit,
  };
};
